import type { ListTileSpec } from "../ListTiles";
import type { ChartChip } from "../ChartChips";
import { fmtCount } from "../listMath";

export interface ClientTileRow {
  status: string | null;
  overallProgress: number;
  holdRevisitDate: string | null;
  closing: { outcome: string } | null;
}

const isActive = (r: ClientTileRow) => r.status === "Active" || !r.status;
/** "In market" keeps the page's existing rule: an active engagement past 30% progress. */
const inMarket = (r: ClientTileRow) => isActive(r) && r.overallProgress > 30;
const shortDay = (ymd: string) => new Date(`${ymd.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function clientTiles(rows: ClientTileRow[], now: Date): ListTileSpec[] {
  const active = rows.filter(isActive);
  const market = active.filter(inMarket).length;
  const hold = rows.filter((r) => r.status === "On Hold");
  const today = now.toISOString().slice(0, 10);
  const nextRevisit = hold.map((r) => r.holdRevisitDate?.slice(0, 10)).filter((d): d is string => !!d && d >= today).sort()[0];
  const closed = rows.filter((r) => r.status === "Closed");
  const sold = closed.filter((r) => r.closing?.outcome === "Sold").length;
  const unrecorded = closed.filter((r) => !r.closing).length;
  return [
    { key: "active", label: "Active", value: active.length, sub: `${fmtCount(market)} in market`,
      coverage: { covered: market, total: active.length, title: `${fmtCount(market)} in market, ${fmtCount(active.length - market)} preparing` } },
    { key: "hold", label: "On hold", value: hold.length, sub: nextRevisit ? `next revisit ${shortDay(nextRevisit)}` : "no revisit date set" },
    { key: "closed", label: "Closed", value: closed.length,
      sub: unrecorded > 0 ? `${fmtCount(sold)} sold · outcome not recorded for ${fmtCount(unrecorded)}` : `${fmtCount(sold)} sold` },
  ];
}

export function clientStatusChips(rows: ClientTileRow[]): ChartChip[] {
  return [
    { key: "Active", label: "Active", count: rows.filter(isActive).length },
    { key: "On Hold", label: "On hold", count: rows.filter((r) => r.status === "On Hold").length },
    { key: "Closed", label: "Closed", count: rows.filter((r) => r.status === "Closed").length },
  ];
}
